import {inject, Injectable} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ZynyoApiAccount } from '@shared/models/api-account/zynyo.api.account';
import { DataService } from '../data/data.service';
import { UserService } from '@services/user/user.service';
import { SettingsService } from '@services/settings/settings.service';
import { ModalContentComponent } from '@shared/components/modal-content/modal-content.component';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AuthorizationService } from '@services/authorization/authorization.service';
import { ErrorDisplayService } from '@services/error-display/error-display.service';


@Injectable({
    providedIn: 'root'
})
export class ApiAccountsService extends DataService {


    protected readonly userService = inject(UserService);
    protected readonly modalService = inject(BsModalService);
    protected readonly settingsService = inject(SettingsService);
    protected readonly authorizationService = inject(AuthorizationService);
    protected readonly translateService = inject(TranslateService);
    protected readonly errorDisplayService  = inject(ErrorDisplayService)


    constructor() {
        super();
    }

    override get url(): string {
        const base = this.configService.config?.apiBaseUrl ?? '';
        return `${base}apiaccounts`;
    }

    private _apiAccounts!: ZynyoApiAccount[];

    get apiAccounts(): ZynyoApiAccount[] {
        return this._apiAccounts;
    }

    set apiAccounts(value: ZynyoApiAccount[]) {
        this._apiAccounts = value;
    }

    loadEnabledApiKeys(): Promise<ZynyoApiAccount[]> {
        return this.getAll<ZynyoApiAccount[]>('', { enabled: true })
            .toPromise()
            .then((res) => {

                // If res.length is 0, that means there is no linked Signingservice account.
                if (res) {
                    if (res.length === 0) {
                        const errorMsg = this.translateService.instant('error.unlinked_account');
                        const errorTitle = this.translateService.instant('error.unlinked_account_title');

                        const initialState = {
                            message: errorMsg,
                            title: errorTitle,
                            backdrop: true,
                            ignoreBackDropClick: true,
                            closeBtn: false
                        };

                        this.modalService.show(ModalContentComponent, { initialState });
                        return Promise.reject(new Error('No API accounts found'));
                    }

                    // Filter api accounts result if departFilter is on
                    const roles: string[] = this.authorizationService.roles;
                    const viewAll = roles.indexOf('view_all') > -1;
                    if (this.settingsService.settings.departmentFilter
                        && !viewAll && this.settingsService.settings.apiAccountsSeparator) {
                        res = res.filter(apiaccounts => apiaccounts.name === this.userService.department);
                    }

                    let select: ZynyoApiAccount | null;
                    if (this.userService.department && res.length > 1 && this.settingsService.settings.apiAccountsSeparator) {
                        select = (res.find(apiAccount => apiAccount.name === this.userService.department)) ?? res[0];
                    } else if (this.settingsService.settings.apikey) {
                        select = res.find(val => val.apiKey === this.settingsService.settings.apikey) ?? res[0];
                    } else {
                        select = res[0];
                    }

                    this.ApiKey = select?.apiKey ?? '';
                    res = res.sort((a, b) => a.name?.localeCompare(b.name));
                    this.apiAccounts = res;
                }
                return res;
            }, (error: HttpErrorResponse) => {
                // const errorDisplayService = this.injector.get(ErrorDisplayService);

                if (error.status === 401) {
                    const errorMsg = this.translateService.instant('error.disabled_account');
                    const errorTitle = this.translateService.instant('error.disabled_account_title');

                    const initialState = {
                        message: errorMsg,
                        title: errorTitle,
                        backdrop: true,
                        ignoreBackDropClick: true,
                        closeBtn: false
                    };

                    this.modalService.show(ModalContentComponent, { initialState });
                } else {
                    this.errorDisplayService.displayError(this.translateService.instant('error.server_unavailable.title'), error.message);
                }
                return error as any;
            });
    }
}
